import React from "react";

/* ═══════════════════════════════════════════
   SHARED FLOW VISUALS — reusable across pages
   Grid background + Trace progress + Flow badge
   ═══════════════════════════════════════════ */

/* ── Subtle PCB grid background (full-page SVG overlay) ── */
export function FlowGrid({ opacity = 0.025 }) {
  return (
    <svg style={{ position: "fixed", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 0 }}
      viewBox="0 0 1000 600" preserveAspectRatio="xMidYMid slice">
      <g opacity={opacity}>
        {[80, 160, 240, 320, 400, 480, 560].map((y, i) => (
          <line key={`h-${i}`} x1={0} y1={y} x2={1000} y2={y} stroke="#A1A1AA" strokeWidth={0.4} strokeDasharray="3 10" />
        ))}
        {[120, 240, 360, 480, 620, 740, 860].map((x, i) => (
          <line key={`v-${i}`} x1={x} y1={0} x2={x} y2={600} stroke="#A1A1AA" strokeWidth={0.4} strokeDasharray="3 10" />
        ))}
        {[120, 360, 620, 860].flatMap(x => [160, 320, 480].map(y => (
          <circle key={`n-${x}-${y}`} cx={x} cy={y} r={1.2} fill="#A1A1AA" opacity={0.5} />
        )))}
      </g>
    </svg>
  );
}

/* ── Trace-style stage progress (horizontal pipeline) ── */
export function TraceProgress({ stages, small = false }) {
  const size = small ? 18 : 22;
  const fontSize = small ? "0.5rem" : "0.6rem";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: small ? 1 : 2 }}>
      {stages.map((stage, i) => {
        const isActive = stage.status === "active";
        const isDone = stage.status === "completed";
        return (
          <div key={stage.stage_key || i} data-testid={`trace-stage-${stage.stage_key || i}`}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: small ? "3px 6px" : "5px 8px",
              borderRadius: 8,
              background: isActive ? "var(--accent-subtle)" : "transparent",
              transition: "background 0.3s ease",
            }}>
            {/* Trace node */}
            <div style={{
              width: size, height: size, borderRadius: "50%", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: isDone ? "var(--primary)" : isActive ? "var(--primary)" : "var(--bg-soft)",
              border: isDone || isActive ? "none" : "1.5px solid var(--bg-neutral)",
              boxShadow: isActive ? "0 0 10px rgba(0,92,238,0.2)" : "none",
              transition: "all 0.3s ease",
            }}>
              {isDone ? (
                <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 12 12" fill="none">
                  <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                <div style={{
                  width: size * 0.3, height: size * 0.3, borderRadius: "50%",
                  background: isActive ? "#fff" : "var(--bg-neutral)",
                }} />
              )}
            </div>
            {/* Trace connector line (vertical, shown between nodes) */}
            <span style={{
              fontSize, fontWeight: isActive ? 700 : isDone ? 500 : 400,
              color: isActive ? "var(--primary)" : isDone ? "var(--text-secondary)" : "var(--text-muted)",
              fontFamily: "'Satoshi', sans-serif",
              transition: "all 0.3s ease",
            }}>
              {stage.stage_label || stage.label}
            </span>
            {isActive && (
              <span style={{
                marginLeft: "auto", width: 5, height: 5, borderRadius: "50%",
                background: "var(--primary)", animation: "flowPulse 2s ease-in-out infinite",
              }} />
            )}
          </div>
        );
      })}
      <style>{`@keyframes flowPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>
    </div>
  );
}

/* ── Flow complexity badge (reusable) ── */
export function FlowBadge({ count, style = {} }) {
  return (
    <div data-testid="flow-badge" style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 100,
      background: "var(--violet-subtle)", border: "1px solid rgba(138,79,255,0.12)",
      ...style,
    }}>
      <span style={{
        width: 5, height: 5, borderRadius: "50%",
        background: count > 6 ? "var(--violet)" : "var(--primary)",
        animation: "flowPulse 2s ease-in-out infinite",
      }} />
      <span style={{
        fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem",
        fontWeight: 700, color: "var(--violet)", letterSpacing: "0.05em",
      }}>
        {count} {count === 1 ? "stream" : "streams"}
      </span>
    </div>
  );
}

/* ── Mini flow indicator (shows active flow types as colored dots) ── */
const FLOW_COLORS = { d: "#005CEE", i: "#8A4FFF", t: "#FF3366", e: "#005CEE", f: "#8A4FFF" };
const FLOW_LABELS = { d: "Data", i: "Intelligence", t: "Tools", e: "Execution", f: "Feedback" };

export function FlowIndicator({ flowTypes }) {
  if (!flowTypes || flowTypes.length === 0) return null;
  const unique = [...new Set(flowTypes)];
  return (
    <div data-testid="flow-indicator" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      {unique.map(type => (
        <div key={type} style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: FLOW_COLORS[type] || "#A1A1AA" }} />
          <span style={{
            fontFamily: "'JetBrains Mono', monospace", fontSize: "0.52rem",
            fontWeight: 600, color: "var(--text-muted)", letterSpacing: "0.06em",
          }}>{FLOW_LABELS[type] || type}</span>
        </div>
      ))}
    </div>
  );
}
